import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_ALLOWED_FILE_TYPES } from "../../../../classes/constants/fileConstants";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { useNotification } from "../../../../classes/contexts/NotificationContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { FileImportState } from "../../../../classes/enums/ModalModalityEnum";
import { SnackbarType } from "../../../../classes/enums/SnackbarTypeEnum";
import { groupFilesById } from "../../../../classes/utils/FileUtils";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useDuplicateDetection } from "../../../../hooks/useDuplicateDetection";
import { useFileManagement } from "../../../../hooks/useFileManagement";
import { useFileValidation } from "../../../../hooks/useFileValidation";
import { useImageImport } from "../../../../hooks/useImageImport";
import { useImportNotification } from "../../../../hooks/useImportNotification";
import { useLoading } from "../../../../hooks/useLoading";
import { getPatientNameMismatch } from "../../../../hooks/usePatientNameMismatch";
import { useRouteState } from "../../../../hooks/useRouteState";
import ImageValidation from "../../../common/uploadModal/imageValidation/ImageValidation";
import PatientMetadataMismatchModal from "../patientMetadataMismatchModal/PatientMetadataMismatchModal";
import FileUploadBase from "../fileUploadBase/FileUploadBase";
import ModalBase from "../modalBase/ModalBase";
import "./FileImport.css";

const FileImport = ({
  onSave,
  onProcessingComplete: onProcessingCompleteExternal,
  allowedFileTypes = DEFAULT_ALLOWED_FILE_TYPES,
}) => {
  const { hidePopup } = useAppContext();
  const { patientData, refreshPatientContext, fetchPatientImages } =
    usePatientContext();

  const { params } = useRouteState();
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [state, setState] = useState(FileImportState.upload);
  const [isNameMismatchModalOpen, setIsNameMismatchModalOpen] = useState(false);
  const [nameMismatchSubject, setNameMismatchSubject] = useState(null);
  const previousDuplicateSignatureRef = useRef("");
  const wasInValidationRef = useRef(false);

  const studyIds = useMemo(
    () => patientData?.studies || [],
    [patientData?.studies]
  );

  const fileManagement = useFileManagement({
    uploadedFiles,
    setUploadedFiles,
    allowedFileTypes,
    setIsImporting,
    setImportProgress,
  });

  const {
    isLoading,
    progress,
    progressMessage,
    startLoading,
    updateProgress,
    finishLoading,
  } = useLoading();

  const {
    startImportNotification,
    updateImportStage,
    completeImportNotification,
  } = useImportNotification();

  const { validateGroups } = useFileValidation();

  useEffect(() => {
    const { problematicGroups } = groupFilesById(uploadedFiles);
    validateGroups(problematicGroups);
  }, [uploadedFiles, validateGroups]);

  const { duplicateGroupIds, hasDuplicates } = useDuplicateDetection(
    studyIds,
    uploadedFiles
  );

  const duplicateSignature = useMemo(
    () => Array.from(duplicateGroupIds).sort().join("|"),
    [duplicateGroupIds]
  );

  const uploadedFilesMismatchInfo = useMemo(
    () => getPatientNameMismatch(patientData, uploadedFiles),
    [patientData, uploadedFiles]
  );

  useEffect(() => {
    if (!duplicateSignature) {
      previousDuplicateSignatureRef.current = "";
      return;
    }

    if (duplicateSignature !== previousDuplicateSignatureRef.current) {
      addNotification(SnackbarType.WARNING, t("463"), { duration: 0 });
      previousDuplicateSignatureRef.current = duplicateSignature;
    }
  }, [addNotification, duplicateGroupIds, duplicateSignature, t]);

  useEffect(() => {
    const isValidationStep = state === FileImportState.validation;
    if (isValidationStep && !wasInValidationRef.current && hasDuplicates) {
      addNotification(SnackbarType.WARNING, t("464"), { duration: 0 });
    }
    wasInValidationRef.current = isValidationStep;
  }, [addNotification, duplicateGroupIds, hasDuplicates, state, t]);

  const { handleImport } = useImageImport({
    patientId: params.patientId,
    onSave,
    setIsImporting: importing => {
      // Only start notification when beginning import, not when finishing
      if (importing) {
        startLoading(t("273"));
        setIsImporting(true);
        // Start the persistent notification (will track through processing)
        startImportNotification(t("273"));
      } else {
        setIsImporting(false);
      }
    },
    setImportProgress: (progress, message) => {
      setImportProgress(progress);
      const progressMsg = message || `${t("274")} ${progress}%`;
      updateProgress(progress, progressMsg);
    },
    onStageChange: stage => {
      // Update notification with current stage (uploading, processing, complete)
      updateImportStage(stage);
    },
    setState,
    successState: null, // No success state - popup closes immediately, notification shows status
    errorState: FileImportState.error,
    // Called when upload is done (UI can proceed, popup closes)
    onComplete: success => {
      finishLoading();
      if (success) {
        refreshPatientContext();
        fetchPatientImages();
        hidePopup(); // Close immediately - notification handles success/error display
      } else {
        // Upload failed - complete notification with error
        completeImportNotification(false, t("232"));
      }
    },
    // Called when backend processing is done (notification updates)
    onProcessingComplete: async success => {
      completeImportNotification(success, success ? t("230") : t("232"));
      // Refresh patient data after processing completes
      if (success) {
        await refreshPatientContext();
        await fetchPatientImages();
      }
      await onProcessingCompleteExternal?.(success);
    },
  });

  const handleValidatedImport = processedGroups => {
    console.log("[FileImport] handleValidatedImport", {
      processedGroupsCount: processedGroups?.length || 0,
    });
    handleImport(processedGroups);
  };

  const handleCloseNameMismatchModal = () => {
    console.log("[FileImport] closing patient metadata mismatch modal");
    setIsNameMismatchModalOpen(false);
    setNameMismatchSubject(null);
  };

  const handleContinueAfterNameMismatch = () => {
    console.log("[FileImport] continuing to validation after mismatch warning", {
      mismatchSubject: nameMismatchSubject,
    });

    setIsNameMismatchModalOpen(false);
    setNameMismatchSubject(null);
    setState(FileImportState.validation);
  };

  const handleProceed = () => {
    console.log("[FileImport] handleProceed", {
      uploadedFilesCount: uploadedFiles.length,
      uploadedFilesMismatchInfo,
    });

    if (uploadedFiles.length > 0) {
      if (uploadedFilesMismatchInfo.hasNameMismatch) {
        console.log("[FileImport] opening patient metadata mismatch modal");
        setNameMismatchSubject(uploadedFilesMismatchInfo.mismatchSubject);
        setIsNameMismatchModalOpen(true);
        return;
      }

      setState(FileImportState.validation);
    } else {
      alert(t("234"));
    }
  };

  const renderContent = () => {
    const { groups: groupedFiles } = groupFilesById(uploadedFiles);

    switch (state) {
      case FileImportState.upload:
        return (
          <FileUploadBase
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            isImporting={isImporting}
            setIsImporting={setIsImporting}
            importProgress={importProgress}
            setImportProgress={setImportProgress}
            allowedFileTypes={allowedFileTypes}
            onProceed={handleProceed}
            fileHandlers={fileManagement}
          />
        );

      case FileImportState.validation:
        return (
          <ImageValidation
            groups={groupedFiles}
            onImport={handleValidatedImport}
            onBack={() => setState(FileImportState.upload)}
            onRemoveFile={fileManagement.removeFile}
          />
        );

      case FileImportState.error:
        return (
          <div className="error-message">
            <div className="modal-title">{t("231")}</div>
            <div className="modal-subtitle">{t("232")}</div>
            <button
              className="retry-button"
              onClick={() => setState(FileImportState.upload)}
            >
              {t("233")}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  const getSubtitle = () => {
    if (state === FileImportState.upload) {
      const firstName = patientData?.first_name || "";
      const lastName = patientData?.last_name || "";
      const patientName = `${firstName} ${lastName}`.trim();

      return `${t("197")}: ${patientName}`;
    }
    return "";
  };

  const handleGoBack = () => {
    setState(FileImportState.upload);
  };

  const shouldShowGoBack = () => {
    return state === FileImportState.validation;
  };

  return (
    <ModalBase
      subtitle={getSubtitle()}
      onClose={hidePopup}
      showGoBack={shouldShowGoBack()}
      onGoBack={handleGoBack}
      tooltipGoBack={t("235")}
      isLoading={isLoading || isImporting}
      progress={progress || importProgress}
      progressMessage={progressMessage}
      className="file-import-modal"
    >
      {renderContent()}
      {isNameMismatchModalOpen && nameMismatchSubject && (
        <PatientMetadataMismatchModal
          patientName={[
            patientData?.first_name,
            patientData?.last_name,
          ]
            .filter(Boolean)
            .join(" ")
            .trim() || t("123")}
          mismatchSubject={nameMismatchSubject}
          onClose={handleCloseNameMismatchModal}
          onContinue={handleContinueAfterNameMismatch}
        />
      )}
    </ModalBase>
  );
};

export default FileImport;
